import useSWR from "swr";
import { useAuthContext } from "src/auth/hooks";
import axios, { fetcher } from "src/utils/axios";
import type { AudienceOption, BulkContent, BulkHistoryItem, BulkPreview, CommunicationAudience, LegalDocument, PendingLegalDocument } from "src/types/federation-communication";

export const communicationBase = (provider: number) => `/providers/${provider}/federation/communications`;
const legalBase = (provider: number) => `/providers/${provider}/federation/legal/documents`;
const get = ([url]: [string, ...unknown[]]) => fetcher(url);

function usePrivateData<T>(url: string | null) {
  const { user } = useAuthContext();
  return useSWR<T>(url && user?.id ? [url, user.id] : null, get, { keepPreviousData: false, shouldRetryOnError: false });
}
export function useCommunicationCapabilities(provider: number, enabled: boolean) {
  return usePrivateData<{ allowed_actions: string[] }>(provider && enabled ? `${communicationBase(provider)}/capabilities` : null);
}
export function useCommunicationAudiences(provider: number, kind: CommunicationAudience, query: string, enabled: boolean) {
  return usePrivateData<{ items: AudienceOption[]; has_more: boolean }>(enabled && provider ? `${communicationBase(provider)}/audiences/${kind}?q=${encodeURIComponent(query)}` : null);
}
export function useBulkHistory(provider: number, enabled: boolean, offset = 0) {
  return usePrivateData<{ items: BulkHistoryItem[]; has_more: boolean }>(enabled && provider ? `${communicationBase(provider)}/bulk?offset=${offset}` : null);
}
export function useLegalDocuments(provider: number, enabled: boolean) {
  return usePrivateData<{ items: LegalDocument[] }>(enabled && provider ? legalBase(provider) : null);
}
export function usePendingLegalDocuments() {
  const { user } = useAuthContext();
  return useSWR<{ items: PendingLegalDocument[] }>(user?.id ? ["/me/federation/legal/pending", user.id] : null, get,
    { keepPreviousData: false, shouldRetryOnError: false, revalidateOnFocus: true, revalidateOnReconnect: true, refreshInterval: 60000 });
}
export async function previewBulk(provider: number, payload: BulkContent) {
  return (await axios.post<BulkPreview>(`${communicationBase(provider)}/bulk/preview`, payload)).data;
}
export async function communicationCommand<T = unknown>(url: string, payload: object) {
  return (await axios.post<T>(url, payload)).data;
}
export { legalBase };
